import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Switch,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
} from "@mui/material";
import {
  parseBundle,
  type Bundle,
  type BundleRequirement,
  type Product,
} from "../database/db";
import { useSettings } from "../contexts/SettingsContext";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { formatCurrency } from "../utils/formatters";

interface BundleFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (bundle: Omit<Bundle, "id">) => void;
  initialBundle?: Bundle;
  products: Product[];
}

interface ProductSelection {
  product: Product;
  selected: boolean;
  quantity: number;
}

export default function BundleForm({
  open,
  onClose,
  onSave,
  initialBundle,
  products,
}: BundleFormProps) {
  const { settings } = useSettings();
  const [name, setName] = useState("");
  const [productSelections, setProductSelections] = useState<
    ProductSelection[]
  >(() =>
    products.map((product) => ({
      product,
      selected: false,
      quantity: 1,
    }))
  );
  const [salePrice, setSalePrice] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const resetForm = useCallback(() => {
    setName("");
    setSalePrice("");
    setProductSelections(
      products.map((product) => ({
        product,
        selected: false,
        quantity: 1,
      }))
    );
    setErrors({});
  }, [products]);

  const calculateTotalPrice = useCallback(
    (productSelections: ProductSelection[]): number => {
      return productSelections.reduce(
        (total, { selected, quantity, product }) => {
          return total + (selected ? product.price * quantity : 0);
        },
        0
      );
    },
    []
  );

  const totalPrice = useMemo(
    () => calculateTotalPrice(productSelections),
    [calculateTotalPrice, productSelections]
  );

  const discount = useMemo((): {
    amount: number;
    percentage: number;
  } => {
    const enteredSalePrice = Number(salePrice) || 0;
    const discountAmount = totalPrice - enteredSalePrice;
    const discountPercentage = (discountAmount / totalPrice) * 100;
    return {
      amount: discountAmount,
      percentage: discountPercentage,
    };
  }, [salePrice, totalPrice]);

  const handleQuantityChange = useCallback(
    (index: number, newQuantity: number) => {
      setProductSelections((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, quantity: Math.max(1, newQuantity) } : item
        )
      );
    },
    []
  );

  const handleToggleProduct = useCallback((index: number) => {
    setProductSelections((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, selected: !item.selected } : item
      )
    );
  }, []);

  const getFormErrors = useCallback(
    (
      nameValue: string,
      selections: ProductSelection[],
      priceValue: string
    ): { [key: string]: string } => {
      const errors: { [key: string]: string } = {};

      if (!nameValue.trim()) {
        errors.name = "Name is required";
      }

      const selectedProducts = selections.filter((p) => p.selected);
      if (selectedProducts.length === 0) {
        errors.products = "Select at least one product";
      }

      const enteredPrice = Number(priceValue);
      if (!priceValue) {
        errors.salePrice = "Bundle price is required";
      } else if (isNaN(enteredPrice)) {
        errors.salePrice = "Please enter a valid number";
      } else if (enteredPrice >= totalPrice) {
        errors.salePrice = "Bundle price must be less than total price";
      }

      return errors;
    },
    [totalPrice]
  );

  const handleSubmit = useCallback(() => {
    const formErrors = getFormErrors(name, productSelections, salePrice);
    if (Object.keys(formErrors).length === 0) {
      const requirements: BundleRequirement = productSelections
        .filter(({ selected }) => selected)
        .map(({ product, quantity }) => ({
          count: quantity,
          requiredProductId: product.id,
        }));

      onSave({
        name,
        requirements: JSON.stringify(requirements),
        discountAmount: discount.amount,
      });
      resetForm();
      onClose();
    } else {
      setErrors(formErrors);
    }
  }, [
    discount.amount,
    getFormErrors,
    name,
    onClose,
    onSave,
    productSelections,
    resetForm,
    salePrice,
  ]);

  useEffect(() => {
    if (initialBundle) {
      setName(initialBundle.name);
      const requirements = parseBundle(initialBundle).requirements;
      const newProductSelections = products.map((product) => ({
        product,
        selected: requirements.some(
          (req) => req.requiredProductId === product.id
        ),
        quantity:
          requirements.find((req) => req.requiredProductId === product.id)
            ?.count || 1,
      }));
      setProductSelections(newProductSelections);
      setSalePrice(
        (
          calculateTotalPrice(newProductSelections) -
          initialBundle.discountAmount
        ).toString()
      );
    } else {
      resetForm();
    }
  }, [calculateTotalPrice, initialBundle, products, resetForm]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <LocalOfferIcon color="primary" />
        {initialBundle ? "Edit" : "Create"} Bundle
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="Bundle Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            required
          />

          {errors.products && <Alert severity="error">{errors.products}</Alert>}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {productSelections.map((selection, index) => (
              <Box
                key={selection.product.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }}>
                <Switch
                  checked={selection.selected}
                  onChange={() => handleToggleProduct(index)}
                />

                {selection.product.imageBlob && (
                  <img
                    src={URL.createObjectURL(selection.product.imageBlob)}
                    alt={selection.product.name}
                    style={{ width: 50, height: 50, objectFit: "contain" }}
                  />
                )}

                <Typography sx={{ flex: 1 }}>
                  {selection.product.name}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleQuantityChange(index, selection.quantity - 1)
                    }
                    disabled={!selection.selected}>
                    <RemoveIcon />
                  </IconButton>

                  <TextField
                    value={selection.quantity}
                    onChange={(e) =>
                      handleQuantityChange(index, parseInt(e.target.value) || 1)
                    }
                    disabled={!selection.selected}
                    size="small"
                    sx={{ width: 60 }}
                    inputProps={{ min: 1, style: { textAlign: "center" } }}
                  />

                  <IconButton
                    size="small"
                    onClick={() =>
                      handleQuantityChange(index, selection.quantity + 1)
                    }
                    disabled={!selection.selected}>
                    <AddIcon />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
            <Typography variant="subtitle1">
              Total Value: {formatCurrency(totalPrice, settings)}
            </Typography>

            <TextField
              label="Bundle Sale Price"
              value={salePrice}
              onChange={(e) => setSalePrice(e.target.value)}
              error={!!errors.salePrice}
              helperText={errors.salePrice}
              fullWidth
              required
              type="number"
              sx={{ mt: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {settings.currencySymbol}
                  </InputAdornment>
                ),
              }}
            />

            {salePrice && !errors.salePrice && (
              <Box sx={{ mt: 2 }}>
                {discount.amount > 0 ? (
                  <Typography color="success.main">
                    You save: {formatCurrency(discount.amount, settings)} (
                    {discount.percentage.toFixed(1)}%)
                  </Typography>
                ) : (
                  <Typography color="error.main">
                    Not a valid discount: Price must be lower than total value
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          Save Bundle
        </Button>
      </DialogActions>
    </Dialog>
  );
}
